/**
 * Panneau « Charte et logo » de Mon entreprise : logo, couleurs, police, textes d'en-tête et de pied de
 * page des documents du client, avec aperçu avant enregistrement. Composants et classes existants
 * uniquement ; aucune modification de la charte graphique de l'application Nexxus.
 */
import {
  BRANDING_TEXT_MAX,
  DEFAULT_BRAND_PRIMARY,
  DEFAULT_BRAND_SECONDARY,
  LOGO_POSITIONS,
  brandingLogoFromBytes,
  normalizeCompanyBranding,
  type CompanyBranding,
  type CompanyBrandingLogo,
  type LogoPosition,
} from '@domain/branding/CompanyBranding';
import { normalizeHexColor } from '@domain/branding/colors';
import {
  DEFAULT_FONT_KEY,
  SAFE_FONTS,
  isSafeFontKey,
  type SafeFontKey,
} from '@domain/branding/fonts';
import { LogoRejectedError, LOGO_MAX_DIMENSION } from '@domain/branding/logoSanitizer';
import type { Tenant } from '@shared/types/Tenant';
import { buildBulletinHTML } from '@presentation/print/bulletinTemplate';
import { buildAttestationHTML } from '@presentation/print/attestationTemplate';
import { esc } from '@presentation/shell/format';
import { APERCU_BULLETIN, APERCU_SALARIE } from './charteApercuExemple';

export interface CharteClientDeps {
  /** Entreprise courante (identité imprimée dans l'aperçu). */
  getTenant: () => Tenant;
  /** Admin / RH uniquement ; sinon le panneau est en lecture seule. */
  canMutate: boolean;
  /** 512 Ko, ou 160 Ko en Hybride (limite de fiche de la synchronisation). */
  maxLogoBytes: number;
  /** Enregistre la charte validée, ou la supprime (`null` = charte par défaut). */
  save: (branding: CompanyBranding | null) => Promise<void>;
  confirmReset: () => Promise<boolean>;
  notify: { success: (m: string) => void; error: (m: string) => void };
}

interface Draft {
  logo: CompanyBrandingLogo | undefined;
  logo_position: LogoPosition;
  primary: string;
  secondary: string;
  font: SafeFontKey;
  header_text: string;
  footer_text: string;
}

const POSITION_LABELS: Record<LogoPosition, string> = {
  left: 'À gauche',
  center: 'Centré',
  right: 'À droite',
};

function draftFrom(b: CompanyBranding | undefined): Draft {
  return {
    logo: b?.logo,
    logo_position: b?.logo_position ?? 'left',
    primary: b?.primary_color ?? DEFAULT_BRAND_PRIMARY,
    secondary: b?.secondary_color ?? DEFAULT_BRAND_SECONDARY,
    font: b?.font ?? DEFAULT_FONT_KEY,
    header_text: b?.header_text ?? '',
    footer_text: b?.footer_text ?? '',
  };
}

function toCandidate(d: Draft): Record<string, unknown> {
  return {
    version: 1,
    ...(d.logo ? { logo: d.logo } : {}),
    logo_position: d.logo_position,
    primary_color: d.primary,
    secondary_color: d.secondary,
    font: d.font,
    ...(d.header_text.trim() ? { header_text: d.header_text } : {}),
    ...(d.footer_text.trim() ? { footer_text: d.footer_text } : {}),
  };
}

function kb(bytes: number): string {
  return `${String(Math.round(bytes / 1024))} Ko`;
}

export function mountCharteClientPanel(host: HTMLElement, deps: CharteClientDeps): void {
  const initial = deps.getTenant().branding;
  let draft = draftFrom(initial);
  let busy = false;
  const ro = deps.canMutate ? '' : ' disabled';

  host.innerHTML = `
    <div class="card">
      <div class="card-title">Logo et charte graphique de l’entreprise</div>
      <p class="hint" style="margin:0 0 12px;">Le logo et la charte s’appliquent à vos documents : bulletins de paie, attestations (travail, salaire, congé, présence), solde de tout compte, bordereaux, factures et rapports. Les déclarations officielles (DTS CNSS / CNAMGS, DAS) restent à l’identique, ainsi que l’interface de Nexxus.</p>
      <div class="form-grid">
        <div class="field full">
          <label for="charte-logo-file">Logo (PNG ou JPEG, ${kb(deps.maxLogoBytes)} maximum)</label>
          <input id="charte-logo-file" type="file" accept="image/png,image/jpeg"${ro}>
          <span class="hint">Formats PNG et JPEG uniquement (pas de SVG). De 16 à ${String(LOGO_MAX_DIMENSION)} pixels par côté. Les métadonnées de l’image sont supprimées à l’enregistrement.</span>
        </div>
        <div class="field">
          <label>Logo actuel</label>
          <div id="charte-logo-box" style="min-height:48px;display:flex;align-items:center;gap:12px;">
            <img id="charte-logo-img" alt="Logo de l’entreprise" style="max-height:56px;max-width:180px;object-fit:contain;" hidden>
            <span id="charte-logo-none" class="hint">Aucun logo : le logo Nexxus est conservé.</span>
            <button type="button" id="charte-logo-remove" class="btn btn-secondary btn-sm"${ro} hidden>Retirer le logo</button>
          </div>
        </div>
        <div class="field">
          <label for="charte-logo-pos">Position du logo</label>
          <select id="charte-logo-pos"${ro}>${LOGO_POSITIONS.map((p) => `<option value="${p}">${POSITION_LABELS[p]}</option>`).join('')}</select>
        </div>
        <div class="field">
          <label for="charte-primary-hex">Couleur principale</label>
          <div style="display:flex;gap:8px;align-items:center;">
            <input id="charte-primary-picker" type="color" aria-label="Choisir la couleur principale" style="width:48px;padding:2px;"${ro}>
            <input id="charte-primary-hex" type="text" class="mono" maxlength="7" inputmode="text" autocomplete="off" spellcheck="false" placeholder="#0B2545"${ro}>
          </div>
          <span class="hint">En-têtes et bandeaux (texte blanc dessus).</span>
        </div>
        <div class="field">
          <label for="charte-secondary-hex">Couleur secondaire</label>
          <div style="display:flex;gap:8px;align-items:center;">
            <input id="charte-secondary-picker" type="color" aria-label="Choisir la couleur secondaire" style="width:48px;padding:2px;"${ro}>
            <input id="charte-secondary-hex" type="text" class="mono" maxlength="7" inputmode="text" autocomplete="off" spellcheck="false" placeholder="#1F7A8C"${ro}>
          </div>
          <span class="hint">Filets, intitulés de section, accents.</span>
        </div>
        <div class="field">
          <label for="charte-font">Police des documents</label>
          <select id="charte-font"${ro}>${(Object.keys(SAFE_FONTS) as SafeFontKey[]).map((k) => `<option value="${k}">${esc(SAFE_FONTS[k].label)}</option>`).join('')}</select>
          <span class="hint">Polices système sûres, rendu identique sur tous les postes Windows.</span>
        </div>
        <div class="field full">
          <label for="charte-header-text">Ligne d’en-tête complémentaire</label>
          <input id="charte-header-text" type="text" maxlength="${String(BRANDING_TEXT_MAX.header_text)}" placeholder="Ex. Agrément n° 42/MTE — Libreville"${ro}>
          <span class="hint">La raison sociale, l’adresse, le NIF et le RCCM viennent de l’onglet Identification.</span>
        </div>
        <div class="field full">
          <label for="charte-footer-text">Mentions de pied de page</label>
          <textarea id="charte-footer-text" rows="2" maxlength="${String(BRANDING_TEXT_MAX.footer_text)}" style="width:100%;" placeholder="Ex. Siège social, capital, mentions légales…"${ro}></textarea>
        </div>
      </div>
      <div id="charte-issues" class="form-error-summary" role="alert" aria-live="polite" hidden></div>
      <div style="display:flex;gap:8px;margin-top:12px;flex-wrap:wrap;">
        <button type="button" id="charte-save" class="btn btn-primary"${ro}>Enregistrer la charte</button>
        <button type="button" id="charte-reset" class="btn btn-secondary"${ro}>Rétablir la charte par défaut</button>
      </div>
      ${deps.canMutate ? '' : '<p class="hint" style="margin-top:8px;">Seuls l’administrateur et le gestionnaire RH peuvent modifier la charte.</p>'}
    </div>
    <div class="card">
      <div class="card-title">Aperçu avant enregistrement</div>
      <p class="hint" style="margin:0 0 8px;">Données fictives ; seule l’identité de votre entreprise est réelle.</p>
      <div id="charte-preview" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(360px,1fr));gap:16px;align-items:start;"></div>
    </div>`;

  const q = <T extends HTMLElement>(sel: string): T => {
    const el = host.querySelector<T>(sel);
    if (!el) throw new Error(`Élément introuvable : ${sel}`);
    return el;
  };
  const fileInput = q<HTMLInputElement>('#charte-logo-file');
  const logoImg = q<HTMLImageElement>('#charte-logo-img');
  const logoNone = q<HTMLElement>('#charte-logo-none');
  const logoRemove = q<HTMLButtonElement>('#charte-logo-remove');
  const pos = q<HTMLSelectElement>('#charte-logo-pos');
  const font = q<HTMLSelectElement>('#charte-font');
  const pHex = q<HTMLInputElement>('#charte-primary-hex');
  const pPick = q<HTMLInputElement>('#charte-primary-picker');
  const sHex = q<HTMLInputElement>('#charte-secondary-hex');
  const sPick = q<HTMLInputElement>('#charte-secondary-picker');
  const headerText = q<HTMLInputElement>('#charte-header-text');
  const footerText = q<HTMLTextAreaElement>('#charte-footer-text');
  const issues = q<HTMLElement>('#charte-issues');
  const preview = q<HTMLElement>('#charte-preview');
  const saveBtn = q<HTMLButtonElement>('#charte-save');
  const resetBtn = q<HTMLButtonElement>('#charte-reset');

  function showIssues(messages: readonly string[]): void {
    issues.hidden = messages.length === 0;
    issues.innerHTML = messages.map((m) => `<div>${esc(m)}</div>`).join('');
  }

  function syncFields(): void {
    pos.value = draft.logo_position;
    font.value = draft.font;
    pHex.value = draft.primary;
    sHex.value = draft.secondary;
    if (/^#[0-9A-Fa-f]{6}$/.test(draft.primary)) pPick.value = draft.primary.toLowerCase();
    if (/^#[0-9A-Fa-f]{6}$/.test(draft.secondary)) sPick.value = draft.secondary.toLowerCase();
    headerText.value = draft.header_text;
    footerText.value = draft.footer_text;
    if (draft.logo) {
      logoImg.src = `data:${draft.logo.mime};base64,${draft.logo.data}`;
      logoImg.hidden = false;
      logoNone.hidden = true;
      logoRemove.hidden = !deps.canMutate;
    } else {
      logoImg.removeAttribute('src');
      logoImg.hidden = true;
      logoNone.hidden = false;
      logoRemove.hidden = true;
    }
  }

  function renderPreview(): void {
    const result = normalizeCompanyBranding(toCandidate(draft));
    if (!result.ok) {
      showIssues(result.issues.map((i) => i.message));
      preview.innerHTML =
        '<p class="hint">Aperçu indisponible tant que la charte comporte des erreurs.</p>';
      saveBtn.disabled = true;
      return;
    }
    showIssues([]);
    saveBtn.disabled = busy || !deps.canMutate;
    const tenant: Tenant = { ...deps.getTenant(), branding: result.value };
    const bulletin = buildBulletinHTML(APERCU_BULLETIN, APERCU_SALARIE, tenant);
    const attestation = buildAttestationHTML('travail', APERCU_SALARIE, tenant).html;
    preview.innerHTML = `
      <figure style="margin:0;"><figcaption class="hint" style="margin-bottom:4px;">Bulletin de paie</figcaption>
        <div style="zoom:0.5;pointer-events:none;overflow:hidden;" aria-hidden="true">${bulletin}</div></figure>
      <figure style="margin:0;"><figcaption class="hint" style="margin-bottom:4px;">Attestation de travail</figcaption>
        <div style="zoom:0.5;pointer-events:none;overflow:hidden;" aria-hidden="true">${attestation}</div></figure>`;
  }

  function refresh(): void {
    renderPreview();
  }

  // --- saisie
  const readHex = (input: HTMLInputElement, target: 'primary' | 'secondary'): void => {
    const raw = input.value.trim();
    const normalized = normalizeHexColor(raw.startsWith('#') ? raw : `#${raw}`);
    draft[target] = normalized ?? raw;
    if (normalized) {
      (target === 'primary' ? pPick : sPick).value = normalized.toLowerCase();
    }
    refresh();
  };
  pHex.addEventListener('input', () => readHex(pHex, 'primary'));
  sHex.addEventListener('input', () => readHex(sHex, 'secondary'));
  pPick.addEventListener('input', () => {
    draft.primary = pPick.value.toUpperCase();
    pHex.value = draft.primary;
    refresh();
  });
  sPick.addEventListener('input', () => {
    draft.secondary = sPick.value.toUpperCase();
    sHex.value = draft.secondary;
    refresh();
  });
  font.addEventListener('change', () => {
    if (isSafeFontKey(font.value)) draft.font = font.value;
    refresh();
  });
  pos.addEventListener('change', () => {
    const v = LOGO_POSITIONS.find((p) => p === pos.value);
    if (v) draft.logo_position = v;
    refresh();
  });
  headerText.addEventListener('input', () => {
    draft.header_text = headerText.value;
    refresh();
  });
  footerText.addEventListener('input', () => {
    draft.footer_text = footerText.value;
    refresh();
  });
  logoRemove.addEventListener('click', () => {
    draft.logo = undefined;
    fileInput.value = '';
    syncFields();
    refresh();
  });

  fileInput.addEventListener('change', () => {
    void (async () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      try {
        if (file.size > deps.maxLogoBytes) {
          throw new LogoRejectedError('too_large');
        }
        const bytes = new Uint8Array(await file.arrayBuffer());
        draft.logo = brandingLogoFromBytes(bytes, deps.maxLogoBytes);
        syncFields();
        refresh();
      } catch (err) {
        fileInput.value = '';
        refresh();
        if (err instanceof LogoRejectedError) {
          const message =
            err.code === 'too_large'
              ? `Logo trop volumineux : ${kb(deps.maxLogoBytes)} maximum.`
              : err.message;
          showIssues([message]);
        } else {
          showIssues(['Impossible de lire ce fichier.']);
        }
      }
    })();
  });

  saveBtn.addEventListener('click', () => {
    void (async () => {
      if (!deps.canMutate || busy) return;
      const result = normalizeCompanyBranding(toCandidate(draft));
      if (!result.ok) {
        showIssues(result.issues.map((i) => i.message));
        return;
      }
      busy = true;
      saveBtn.disabled = true;
      try {
        await deps.save(result.value);
        draft = draftFrom(result.value);
        syncFields();
        deps.notify.success('Charte graphique enregistrée');
      } catch (err) {
        deps.notify.error(err instanceof Error ? err.message : 'Enregistrement impossible');
      } finally {
        busy = false;
        refresh();
      }
    })();
  });

  resetBtn.addEventListener('click', () => {
    void (async () => {
      if (!deps.canMutate || busy) return;
      const ok = await deps.confirmReset();
      if (!ok) return;
      busy = true;
      try {
        await deps.save(null);
        draft = draftFrom(undefined);
        fileInput.value = '';
        syncFields();
        deps.notify.success('Charte par défaut rétablie');
      } catch (err) {
        deps.notify.error(err instanceof Error ? err.message : 'Opération impossible');
      } finally {
        busy = false;
        refresh();
      }
    })();
  });

  syncFields();
  refresh();
}
